import {
	createContext,
	forwardRef,
	Profiler,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	Button,
	Collapse,
	Label,
	Modal,
	ModalBody,
	ModalHeader,
	Tooltip,
} from "reactstrap";
import moment from "moment";
import { TableVirtuoso } from "react-virtuoso";

import "../../styles/DataValidation.css";
import "../../styles/StackPlots.css";
import { default as utilIcons } from "../../resources/utilIcons";
import { colors } from "../../styles/colors";

import ValidationPlot from "./ValidationPlot";
import { ModalContext, RestContext } from "../../App";
import { VALIDATION_URL } from "../../serverConfig";
import { checkMobile, exists, ifExists } from "../../util/globals";
import SortControls from "../../components/SortControls";
import { deTzTimestamp } from "../../util/timeUtils";
import clamp from "../../util/clamp";
import HeaderSortButtons from "../../components/HeaderSortButtons";
import { rotate } from "ol/transform";

const DataValidationContext = createContext(null);

const COLUMN_LIST = [
	"recordDate",
	"parCode",
	"rawVal",
	"screenFlag",
	"loggerFlag",
	"qualifierCode",
	"validationCode",
	"controlVal",
	"validatedVal",
	"sourceCode",
	"status",
	"insertDate",
	"updateDate",
	"updateBy",
];

const AQMET_COLUMNS = {
	recordDate: "Record Date",
	parCode: "Par Code",
	rawVal: "Raw Val",
	screenFlag: "Screening Flag",
	loggerFlag: "Logger Flag",
	qualifierCode: "Qualifier Code(s)",
	validationCode: "Validation Code",
	controlVal: "Control Val",
	validatedVal: "Validated Val",
	sourceCode: "Source Code",
	status: "Status",
	insertDate: "Insert Date",
	updateDate: "Update Date",
	updateBy: "Update By",
};

const EDITABLE_COLUMNS = {
	recordDate: false,
	parCode: false,
	rawVal: true,
	screenFlag: false,
	loggerFlag: false,
	qualifierCode: true,
	validationCode: false,
	controlVal: false,
	validatedVal: false,
	sourceCode: true,
	status: true,
	insertDate: false,
	updateDate: false,
	updateBy: false,
};

const COLUMN_INDECES = {
	recordDate: 1,
	parCode: 2,
	rawVal: 3,
	screenFlag: 4,
	loggerFlag: 5,
	qualifierCode: 6,
	validationCode: 7,
	controlVal: 8,
	validatedVal: 9,
	sourceCode: 10,
	status: 11,
	insertDate: 12,
	updateDate: 13,
	updateBy: 14,
};

function DataValidation({ selectedSite }) {
	const [dateRange, setDateRange] = useState({
		min: moment()
			.set("hour", 0)
			.set("minute", 0)
			.set("second", 0)
			.set("millisecond", 0)
			.set("date", 1)
			.subtract(1, "month")
			.format("YYYY-MM-DDTHH:mm"),
		max: moment()
			.set("hour", 0)
			.set("minute", 0)
			.set("second", 0)
			.set("millisecond", 0)
			.set("date", 1)
			.format("YYYY-MM-DDTHH:mm"),
	});
	const [parCodeList, setParCodeList] = useState([]);
	const [parCodeRoundingMap, setParCodeRoundingMap] = useState(new Map());
	const [selectedPars, setSelectedPars] = useState([]);
	const [columns, setColumns] = useState([]);
	const [data, setData] = useState([]);
	const [stackplotData, setStackplotData] = useState({});
	const [loading, setLoading] = useState(false);
	const [loadingProgress, setLoadingProgress] = useState(1);
	const [chartRefs, setChartRefs] = useState();
	const [callbacks, setCallbacks] = useState({});
	const [dataChanged, setDataChanged] = useState(false);
	const [dataChanges, setDataChanges] = useState({});
	const [optionsChanged, setOptionsChanged] = useState(false);
	const [highlighted, setHighlighted] = useState({});
	const [sortedData, setSortedData] = useState(null);
	const [dataToSortedMap, setDataToSortedMap] = useState({});
	const [sortedToDataMap, setSortedToDataMap] = useState({});
	const [stackPlotConfig, setStackPlotConfig] = useState(null);
	const [errorCode, setErrorCode] = useState(null); // Oracle error code returned by bad validation request
	const [sortColumns, setSortColumns] = useState([
		["recordDate", "Asc"],
		["parCode", "Asc"],
	]);

	const { sendGetRequest, sendPostRequest } = useContext(RestContext);
	const { openModal, setOpenModal } = useContext(ModalContext);

	const loadingOptions = useRef(loading);
	const stackPlotRef = useRef(null);
	const virtuosoRef = useRef(null);
	const dataRef = useRef([]);

	const toggleFilterModal = () =>
		openModal === "validation-filter-modal"
			? setOpenModal()
			: setOpenModal("validation-filter-modal");

	useEffect(() => {
		setOptionsChanged(selectedPars.length > 0); // Can load data whenever date range is updated or selected parameter list is updated (and not empty)
		resetDataChanges();
		setDataChanged(false);
		if (!!errorCode) {
			setErrorCode(null);
		}
	}, [dateRange, selectedPars]);

	useEffect(() => {
		setSortedData(data);
		const newIndexMap = Object.fromEntries(
			data.map((val, index) => [index, index]),
		);
		setDataToSortedMap(newIndexMap);
		setSortedToDataMap(newIndexMap);
		convertDataForStackPlots(data);
		generateConfig();
		dataRef.current = data;
	}, [data]);

	useEffect(() => {
		//resetTableChanges();
		// applyDataChangesToTable();
	}, [dataToSortedMap]);

	useEffect(() => {
		const sortFn = buildSortFunction(0);
		sortData(sortFn);
	}, [sortColumns]);

	const buildSortFunction = (index) => {
		if (index >= sortColumns.length) {
			return (a, b) => 0;
		}
		const column = sortColumns[index][0];
		const direction = sortColumns[index][1] === "Asc" ? 1 : -1;
		const nextColumnFn = buildSortFunction(index + 1);
		const sortFn = (a, b) => {
			if (!a || !b) return 0;
			else {
				if (a[column] === b[column]) {
					return nextColumnFn(a, b);
				} else {
					if (b[column] === null) return direction;
					else if (a[column] === null) return -direction;
					else return a[column] > b[column] ? direction : -direction;
				}
			}
		};
		return sortFn;
	};

	const sortData = (sortFn) => {
		if (!sortFn) return;
		const newSortedData = [...data];
		newSortedData.sort(sortFn);
		const newDataToSortedMap = {};
		const newSortedToDataMap = {};
		for (let index in newSortedData) {
			const dataIndex = data.indexOf(newSortedData[index]);
			newDataToSortedMap[dataIndex] = index;
			newSortedToDataMap[index] = dataIndex;
		}
		setDataToSortedMap(newDataToSortedMap);
		setSortedToDataMap(newSortedToDataMap);
		setSortedData(newSortedData);
	};

	const convertDataForStackPlots = (data) => {
		if (!data?.length) return;
		const timestamps = [];
		const minTimestamp = moment(data[0].recordDate).unix();
		const maxTimestamp = moment(data[data.length - 1].recordDate).unix();
		for (let i = minTimestamp; i <= maxTimestamp; i += 3600) {
			timestamps.push(i);
		}
		const convertedData = { _timestamps: timestamps };
		for (let row of data) {
			if (!(row.parCode in convertedData)) {
				convertedData[row.parCode] = {};
			}
			const timestamp = moment(row.recordDate).unix();
			convertedData[row.parCode][timestamp] = [
				row.rawVal,
				row.loggerFlag ? row.loggerFlag : row.screenFlag,
			];
			if (!(row.parCode + "_VAL" in convertedData)) {
				convertedData[row.parCode + "_VAL"] = {};
			}
			convertedData[row.parCode + "_VAL"][timestamp] = [
				row.validatedVal,
				row.validationCode,
			];
		}
		setStackplotData(convertedData);
	};

	const beginLoad = () => {
		setDataChanges({});
		const payload = {
			siteId: selectedSite,
			minDate: deTzTimestamp(dateRange.min),
			maxDate: deTzTimestamp(dateRange.max),
			parCodeList: selectedPars.map((par) => par.parCode),
		};
		setLoading(true);
		sendPostRequest(
			VALIDATION_URL + "/getData",
			payload,
			(response) => {
				setData(response.data);
				// setHighlighted(
				//     Object.fromEntries(
				//         response.data.map((row) => [row.recordDate, []])
				//     )
				// ); // Initialize highlighted with timestamps as keys and empty arrays as values
				setLoading(false);
				setOptionsChanged(false);
			},
			(error) => {
				setLoading(false);
			},
		);
	};

	const getMinDate = () => {
		if (!data?.length) return 0;
		return data[0].recordDate;
	};

	const getMaxDate = () => {
		if (!data?.length) return 0;
		return data[data.length - 1].recordDate;
	};

	const getMin = (data) => {
		if (data.length === 0) return -Infinity;
		else if (data.length === 1) return data[0];
		else {
			let min = data[0];
			for (let i = 1; i < data.length; i++) {
				if (data[i] < min) min = data[i];
			}
			return min;
		}
	};

	const getMax = (data) => {
		if (data.length === 0) return Infinity;
		else if (data.length === 1) return data[0];
		else {
			let max = data[0];
			for (let i = 1; i < data.length; i++) {
				if (data[i] > max) max = data[i];
			}
			return max;
		}
	};

	const doXScroll = (scrollOptions) => {
		if (
			!dateRange ||
			dateRange.min === undefined ||
			dateRange.max === undefined ||
			!stackplotData ||
			!stackplotData._timestamps ||
			stackplotData._timestamps.length < 2
		)
			return;
		const { direction, quantity, unit } = scrollOptions;
		let offset = 0;
		switch (direction) {
			case "left":
				let dataMin = moment.unix(stackplotData._timestamps[0]);
				let currentMin = moment(dateRange.min, "YYYY-MM-DDTHH:mm");
				let targetMin = moment(currentMin).subtract(quantity, unit);
				if (targetMin.unix() < dataMin.unix()) {
					offset = currentMin.unix() - dataMin.unix();
				} else {
					offset = currentMin.unix() - targetMin.unix();
				}
				setDateRange({
					min: currentMin
						.subtract(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
					max: moment(dateRange.max, "YYYY-MM-DDTHH:mm")
						.subtract(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
				});
				return;
			case "right":
				let dataMax = moment.unix(
					stackplotData._timestamps[
						stackplotData._timestamps.length - 1
					],
				);
				let currentMax = moment(dateRange.max, "YYYY-MM-DDTHH:mm");
				let targetMax = moment(currentMax).add(quantity, unit);
				if (targetMax.unix() > dataMax.unix()) {
					offset = dataMax.unix() - currentMax.unix();
				} else {
					offset = targetMax.unix() - currentMax.unix();
				}
				setDateRange({
					min: moment(dateRange.min, "YYYY-MM-DDTHH:mm")
						.add(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
					max: currentMax
						.add(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
				});
				return;
			default:
				return;
		}
	};

	const updateTimeframe = (minTimestamp, maxTimestamp) => {
		setDateRange({
			min: minTimestamp,
			max: maxTimestamp,
		});
	};

	const handleMinDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, min: value }));
	};

	const handleMaxDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, max: value }));
	};

	const generateConfig = () => {
		const newConfig = {
			fullTitle: null,
			footnote: null,
			chartSpacing: 20,
			charts: [],
		};
		if (!selectedPars?.length) {
			return newConfig;
		} else {
			for (let par of selectedPars) {
				const parConfig = {
					parCode: par.parCode,
					unit: par.unitCode,
					type: "line",
					side: "L",
					color: "0000FF",
					symbol: null,
					rawPrec: par.rawPrec,
				};
				const chartConfig = {
					leftMin: null,
					leftMax: null,
					rightMin: null,
					rightMax: null,
					chartHeight: 100,
					hasRightSide: false,
					parameters: [parConfig],
				};
				newConfig.charts.push(chartConfig);
			}
		}
		setStackPlotConfig(newConfig);
	};

	const registerCallback = (callbackName, callbackFn) => {
		setCallbacks((values) => ({ ...values, [callbackName]: callbackFn }));
	};

	const scrollTableOnCursor = (timestamp, parCode) => {
		console.log(timestamp);
		if (!virtuosoRef.current) return;
		const ts = moment(timestamp).utc().format("YYYY-MM-DDTHH:mm:ss.SSSZ");
		const data = dataRef.current;
		let l = 0,
			r = data.length,
			m = Math.trunc((l + r) / 2);
		let row;
		while (l < m) {
			row = data[m];
			if (row.recordDate === ts) {
				break;
			} else if (row.recordDate < ts) {
				l = m;
			} else {
				r = m;
			}
			m = Math.trunc((l + r) / 2);
		}
		if (!(row.parCode === parCode && row.recordDate === ts)) {
			l = m - 1;
			r = m + 1;
			let lRow = data[l],
				rRow = data[r];
			while (lRow?.recordDate === ts || rRow?.recordDate === ts) {
				if (lRow?.recordDate === ts && lRow?.parCode === parCode) {
					m = l;
					break;
				} else if (
					rRow?.recordDate === ts &&
					rRow?.parCode === parCode
				) {
					m = r;
					break;
				}
				l--;
				r++;
				lRow = data[l];
				rRow = data[r];
			}
		}
		const tableIndex = dataToSortedMap[m];

		virtuosoRef.current.scrollIntoView({
			index: tableIndex,
			align: "center",
		});
	};

	const onClearCursors = () => {
		setHighlighted({});
	};

	const resetDataChanges = () => {
		//resetTableChanges();
		setDataChanges({});
		setDataChanged(false);
	};

	const buildValidatedValFn = (valCode, controlVal) => {
		if (valCode === "V" || valCode === "VT") {
			return (rawVal) => rawVal;
		} else if (valCode === "VZ") {
			return (rawVal) => 0;
		} else if (valCode === "VA") {
			if (!controlVal?.length) {
				return (rawVal) => rawVal;
			} else if (!isNaN(controlVal[0])) {
				const adjVal = Number(controlVal);
				return (rawVal) => adjVal;
			} else {
				const adjVal = Number(controlVal.slice(1));
				switch (controlVal[0]) {
					case "-":
						return (rawVal) => rawVal - adjVal;
					case "+":
						return (rawVal) => rawVal + adjVal;
					case "/":
						return (rawVal) => rawVal / adjVal;
					case "*":
						return (rawVal) => rawVal * adjVal;
					case "=":
						return (rawVal) => adjVal;
					default:
						return (rawVal) => rawVal;
				}
			}
		} else if (valCode === "VM") {
			if (isNaN(controlVal)) {
				return (rawVal) => -999;
			} else {
				return (rawVal) => Math.min(Number(controlVal), rawVal);
			}
		} else {
			return (rawVal) => -999;
		}
	};

	const applyValidationInfo = (payload) => {
		if (!highlighted) return;
		const { selectedPars, validationCode, qualifierCode, controlVal } =
			payload;
		const highlightedTimestamps = Object.keys(highlighted);
		const minTimestamp = getMin(highlightedTimestamps),
			maxTimestamp = getMax(highlightedTimestamps);

		const validatedValFn = buildValidatedValFn(validationCode, controlVal);

		const newDataChanges = { ...dataChanges };
		for (let index in data) {
			const row = data[index];
			if (row.recordDate < minTimestamp) {
				continue;
			} else if (row.recordDate > maxTimestamp) {
				break;
			} else {
				if (
					highlighted[row.recordDate]?.includes(row.parCode) &&
					selectedPars[row.parCode]
				) {
					const newRow = dataChanges[index]
						? { ...dataChanges[index] }
						: { ...row };
					newRow.validationCode = validationCode;
					newRow.qualifierCode = qualifierCode;
					newRow.controlVal =
						validationCode === "VA" || validationCode === "VM"
							? controlVal
							: null;
					newRow.validatedVal = validatedValFn(newRow.rawVal);
					console.log(
						newRow.rawVal,
						validationCode,
						newRow.validatedVal,
					);
					newDataChanges[index] = newRow;
				}
			}
		}
		// applyDataChangesToTable(); // TODO remove?
		setDataChanges(newDataChanges);

		setDataChanged(true);
	};

	const applyBatchChange = (name, value) => {
		if (!highlighted) return;
		const highlightedTimestamps = Object.keys(highlighted);
		const minTimestamp = getMin(highlightedTimestamps),
			maxTimestamp = getMax(highlightedTimestamps);

		const newDataChanges = { ...dataChanges };
		for (let index in data) {
			const row = data[index];
			if (row.recordDate < minTimestamp) {
				continue;
			} else if (row.recordDate > maxTimestamp) {
				break;
			} else {
				if (highlighted[row.recordDate]?.includes(row.parCode)) {
					const newRow = dataChanges[index]
						? { ...dataChanges[index] }
						: { ...row };
					newRow[name] = value;
					newDataChanges[index] = newRow;
				}
			}
		}
		// applyDataChangesToTable();
		setDataChanges(newDataChanges);

		setDataChanged(true);
	};

	const saveChanges = () => {
		if (!dataChanged) return;

		if (!!errorCode) {
			setErrorCode(null);
		}

		const payload = {
			siteId: selectedSite,
			data: [],
			oldData: [],
		};
		Object.entries(dataChanges).forEach(([key, value]) => {
			payload.data.push(value);
			payload.oldData.push(data[key]);
		});
		sendPostRequest(
			VALIDATION_URL + "/validate",
			payload,
			(response) => {
				const newData = [...data];
				console.log(response.data);
				if (response.data?.length) {
					const dataChangesEntries = Object.entries(dataChanges);
					for (let newRow of response.data) {
						for (let [key, value] of dataChangesEntries) {
							if (
								newRow.parCode === value.parCode &&
								newRow.recordDate === value.recordDate
							) {
								newData[key] = newRow;
								break;
							}
						}
					}
				}

				// Object.entries(dataChanges).forEach(([value, key]) => {
				// 	newData[key] = value;
				// });
				setDataChanges({});
				setData(newData);
			},
			(error) => {
				const code = error.response?.data?.errorCode || null;
				setErrorCode(code);
			},
		);
	};

	const autoValidate = () => {
		const payload = {
			siteId: selectedSite,
			startDate: moment(dateRange.min).toISOString(),
			endDate: moment(dateRange.max).toISOString(),
			parCodes: selectedPars.map((par) => par.parCode),
		};
		sendPostRequest(VALIDATION_URL + "/auto", payload, (response) => {
			beginLoad();
		});
	};

	const autoValidateAll = () => {
		const payload = {
			siteId: selectedSite,
			startDate: moment(dateRange.min).toISOString(),
			endDate: moment(dateRange.max).toISOString(),
			parCodes: [],
		};
		sendPostRequest(VALIDATION_URL + "/auto", payload, (response) => {
			beginLoad();
		});
	};

	const handleHeaderSort = (columnName, direction) => {
		setSortColumns([[columnName, direction]]);
	};

	return (
		<DataValidationContext.Provider
			value={{
				dateRange,
				updateTimeframe,
				parCodeList,
				setParCodeList,
				parCodeRoundingMap,
				selectedPars,
				setSelectedPars,
				handleMinDate,
				handleMaxDate,
				getMinDate,
				getMaxDate,
				doXScroll,
				loading,
				dataChanges,
				setDataChanges,
				dataChanged,
				setDataChanged,
				optionsChanged,
				setOptionsChanged,
				saveChanges,
				autoValidate,
				autoValidateAll,
				highlighted,
				setHighlighted,
				dataToSortedMap,
				sortedToDataMap,
				callbacks,
			}}
		>
			<div id="data-validation">
				<ValidationControls
					selectedSite={selectedSite}
					beginLoad={beginLoad}
					toggleFilterModal={toggleFilterModal}
					errorCode={errorCode}
					setErrorCode={setErrorCode}
				/>
				<div id="validation-content-wrapper">
					<div id="stackplot-controls-help">
						<div className="stackplot-controls-help-blurb">
							<b>Drag:</b> Pan X Axis (all charts)
						</div>
						<div className="stackplot-controls-help-blurb">
							<b>Ctrl+Scroll:</b> Zoom X Axis (all charts)
						</div>
						<div className="stackplot-controls-help-blurb">
							<b>Shift+Drag</b> Pan Y Axis (single chart)
						</div>
						<div className="stackplot-controls-help-blurb">
							<b>Shift+Scroll:</b> Zoom Y Axis (single chart)
						</div>
						<div className="stackplot-controls-help-blurb">
							<b>Shift+Dbl Click:</b> Reset Y Axis (single chart)
						</div>
					</div>
					<ValidationPlot
						data={stackplotData}
						config={stackPlotConfig}
						dateRange={dateRange}
						stackPlotRef={stackPlotRef}
						bubbleChartRefs={setChartRefs}
						callbacks={callbacks}
						highlighted={highlighted}
						setHighlighted={setHighlighted}
						scrollTableOnCursor={scrollTableOnCursor}
						onClearCursors={onClearCursors}
					/>
					<ValidationTable
						data={sortedData ? sortedData : data}
						virtuosoRef={virtuosoRef}
						sortColumns={sortColumns}
						handleHeaderSort={handleHeaderSort}
					/>
				</div>

				<LoadingScreen
					hidden={!loading}
					loadingProgress={loadingProgress}
					isMobile={checkMobile()}
				/>
				<DataFilterModal
					isOpen={openModal === "validation-filter-modal"}
					toggle={toggleFilterModal}
					sortData={sortData}
					sortColumns={sortColumns}
					setSortColumns={setSortColumns}
				/>
				<ValidationPopover
					selectedSite={selectedSite}
					registerCallback={registerCallback}
					highlighted={highlighted}
					applyValidationInfo={applyValidationInfo}
					applyBatchChange={applyBatchChange}
				/>
			</div>
		</DataValidationContext.Provider>
	);
}

function ValidationControls({
	selectedSite,
	beginLoad,
	toggleFilterModal,
	errorCode,
}) {
	const [errorTooltip, setErrorTooltip] = useState(false);
	const {
		dateRange,
		updateTimeframe,
		handleMinDate,
		handleMaxDate,
		getMinDate,
		getMaxDate,
		selectedOptions,
		doXScroll,
		loading,
		dataChanged,
		optionsChanged,
		saveChanges,
		autoValidate,
		autoValidateAll,
		selectedPars,
	} = useContext(DataValidationContext);

	const toggleErrorTooltip = () => {
		if (!!errorCode) {
			setErrorTooltip(!errorTooltip);
		} else {
			setErrorTooltip(false);
		}
	};

	const getErrorMessage = () => {
		switch (errorCode) {
			case 20001:
				return "Validation Codes not starting with 'V' must have Validated Val of -999";
			case 20002:
				return "Validated Val of -999 must not have Validation Code starting with 'V'";
			case 20003:
				return "Raw Val and Validated Val should match for Validation Code 'V'";
			case 20004:
				return "Control Val must not be empty for Validation Code 'VA'";
			case 20005:
				return "Data is locked and cannot be updated.";
			case 20006:
				return "Raw Val cannot be edited unless Source Code is D.";
			default:
				return "Oracle Error";
		}
	};

	const datesInSameMonth = () => {
		if (!dateRange?.min || !dateRange?.max) return false;
		const start = moment(dateRange.min + "+00:00").utcOffset(0, false),
			end = moment(dateRange.max + "+00:00").utcOffset(0, false);
		const inSameMonth =
			start.year() === end.year() &&
			start.month() === end.month() &&
			start < end;
		return inSameMonth;
	};

	return (
		<div id="validation-controls">
			<div id="validation-controls-left">
				<div id="validation-controls-left-top">
					<div className="stackplot-control-wrapper">
						<Label>Timeframe:</Label>
						<StackPlotTimeFrameSelector
							updateTimeframe={updateTimeframe}
						/>
					</div>
					<ParCodeSelector selectedSite={selectedSite} />
					<Button
						id="validation-load-button"
						onClick={beginLoad}
						disabled={!optionsChanged}
					>
						Load Data
					</Button>
				</div>
				<div id="validation-controls-left-bottom">
					<Button
						id="validation-filter-modal-toggle"
						onClick={toggleFilterModal}
					>
						Sort
					</Button>
					<Button
						id="auto-validate-button"
						onClick={autoValidate}
						disabled={
							!datesInSameMonth() ||
							optionsChanged ||
							!selectedPars?.length
						}
					>
						Auto Validate
					</Button>
					<Button
						id="auto-validate-button"
						onClick={autoValidateAll}
						disabled={!datesInSameMonth()}
					>
						Auto Validate All
					</Button>
					<Button
						id="validation-save-button"
						className={!!errorCode ? "error" : ""}
						onClick={saveChanges}
						disabled={!dataChanged}
					>
						Save Changes
					</Button>
					<Tooltip
						isOpen={errorTooltip}
						target="validation-save-button"
						placement="bottom"
						toggle={toggleErrorTooltip}
					>
						{getErrorMessage(errorCode)}
					</Tooltip>
				</div>
			</div>

			<div id="x-zoom-controls" className="stackplot-control-wrapper">
				<Label>View:</Label>
				<input
					type="datetime-local"
					onChange={handleMinDate}
					min={getMinDate()}
					max={dateRange.max || getMaxDate()}
					step={
						selectedOptions
							? selectedOptions.precision.match(/\d+-min/)
								? 60
								: 3600
							: 1
					}
					value={dateRange.min || ""}
				/>
				<span>-</span>
				<input
					type="datetime-local"
					onChange={handleMaxDate}
					min={dateRange.min || getMinDate()}
					max={getMaxDate()}
					step={
						selectedOptions
							? selectedOptions.precision.match(/\d+-min/)
								? 60
								: 3600
							: 1
					}
					value={dateRange.max || ""}
				/>
				<DateScroller doXScroll={doXScroll} />
			</div>
			<Button
				id="stackplots-refresh-button"
				onClick={beginLoad}
				disabled={loading}
			>
				{utilIcons.refresh(colors["ars-neutral-900"])}
			</Button>
		</div>
	);
}

function StackPlotTimeFrameSelector({ updateTimeframe }) {
	const [dateRange, setDateRange] = useState({ min: null, max: null });

	useEffect(() => {
		init();
	}, []);

	const init = () => {
		const newDateRange = {
			min: moment()
				.set("hour", 0)
				.set("minute", 0)
				.set("second", 0)
				.set("millisecond", 0)
				.set("date", 1)
				.subtract(1, "month")
				.format("YYYY-MM-DDTHH:mm"),
			max: moment()
				.set("hour", 0)
				.set("minute", 0)
				.set("second", 0)
				.set("millisecond", 0)
				.set("date", 1)
				.subtract(1, "minute")
				.format("YYYY-MM-DDTHH:mm"),
		};
		setDateRange(newDateRange);
		updateTimeframe(newDateRange.min, newDateRange.max);
	};

	const fireUpdate = (event) => {
		const allowedIds = [
			"stackplot-timeframe-numdays-input",
			"stackplot-timeframe-mindate-input",
			"stackplot-timeframe-maxdate-input",
			"stackplot-timeframe-mode-select",
		];
		if (
			event.relatedTarget?.id &&
			allowedIds.includes(event.relatedTarget.id)
		)
			return;
		if (dateRange.min && dateRange.max) {
			updateTimeframe(dateRange.min, dateRange.max);
		}
	};

	const handleMinDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, min: value }));
	};

	const handleMaxDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, max: value }));
	};

	return (
		<div id="stackplot-timeframe-selector">
			<input
				id="stackplot-timeframe-mindate-input"
				type="datetime-local"
				value={dateRange.min}
				onChange={handleMinDate}
				step={3600}
				onBlur={fireUpdate}
			/>
			<span>to</span>
			<input
				id="stackplot-timeframe-maxdate-input"
				type="datetime-local"
				value={dateRange.max}
				onChange={handleMaxDate}
				onBlur={fireUpdate}
			/>
		</div>
	);
}

function DateScroller({ doXScroll }) {
	const [quantity, setQuantity] = useState(1);
	const [unit, setUnit] = useState("days");

	const handleQuantity = (event) => {
		const value = Number(event.target.value);
		setQuantity(value);
	};

	const handleUnit = (event) => {
		const value = event.target.value;
		setUnit(value);
	};

	const handleMinus = () => {
		doXScroll({
			direction: "left",
			quantity,
			unit,
		});
	};

	const handlePlus = () => {
		doXScroll({
			direction: "right",
			quantity,
			unit,
		});
	};

	return (
		<div className="date-scroller">
			<Button onClick={handleMinus}>-</Button>
			<input
				className="time-quantity-input"
				type="number"
				step={1}
				value={quantity}
				onChange={handleQuantity}
			/>
			<select
				className="time-unit-select"
				value={unit}
				onChange={handleUnit}
			>
				<option value="hours">Hour(s)</option>
				<option value="days">Day(s)</option>
				<option value="weeks">Week(s)</option>
				<option value="months">Month(s)</option>
			</select>
			<Button onClick={handlePlus}>+</Button>
		</div>
	);
}

function ParCodeSelector({ selectedSite }) {
	const [selectedParCodeList, setSelectedParCodeList] = useState([]);
	const [allParCodes, setAllParCodes] = useState(false);
	const [isOpen, setIsOpen] = useState(false);

	const ref = useRef(null);

	const { sendGetRequest, sendPostRequest } = useContext(RestContext);

	const {
		dateRange,
		parCodeList,
		setParCodeList,
		parCodeRoundingMap,
		setSelectedPars,
	} = useContext(DataValidationContext);

	const toggleOpen = () => setIsOpen(!isOpen);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (ref.current && isOpen) {
				if (
					!ref.current.contains(event.target) &&
					event.target.id !== "site-select"
				) {
					toggleOpen();
				}
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [ref, isOpen, toggleOpen]);

	useEffect(() => {
		if (allParCodes) {
			sendGetRequest(VALIDATION_URL + "/parameters/all", (response) => {
				parCodeRoundingMap.clear();
				for (let par of response.data) {
					parCodeRoundingMap.set(par.id.parCode, {
						prec: par.rawPrec,
						trunc: !!par.truncVal,
					});
				}
				setParCodeList(response.data);
			});
		} else {
			if (dateRange?.min && dateRange?.max) {
				const payload = {
					siteId: selectedSite,
					minDate: deTzTimestamp(dateRange.min),
					maxDate: deTzTimestamp(dateRange.max),
				};
				sendPostRequest(
					VALIDATION_URL + "/parameters",
					payload,
					(response) => {
						parCodeRoundingMap.clear();
						for (let par of response.data) {
							parCodeRoundingMap.set(par.id.parCode, {
								prec: par.rawPrec,
								trunc: !!par.truncVal,
							});
						}
						setParCodeList(response.data);
					},
				);
			}
		}
	}, [selectedSite, allParCodes, dateRange]);

	useEffect(() => {
		const newSelected = [];
		for (let par of selectedParCodeList) {
			const foundPar = parCodeList.find(
				(other) => other.parCode === par.parCode,
			);
			if (foundPar) newSelected.push(foundPar);
		}
		newSelected.sort(compPars);
		setSelectedParCodeList(newSelected);
	}, [parCodeList]);

	useEffect(() => {
		setSelectedPars(selectedParCodeList);
	}, [selectedParCodeList]);

	const compPars = (a, b) => {
		return parCodeList.indexOf(a) - parCodeList.indexOf(b);
	};

	const togglePar = (par) => {
		const newSelected = [...selectedParCodeList];
		const foundPar = selectedParCodeList.find(
			(other) => other.parCode === par.parCode,
		);
		if (foundPar) {
			newSelected.splice(newSelected.indexOf(foundPar), 1);
		} else {
			newSelected.push(par);
		}
		newSelected.sort(compPars);
		setSelectedParCodeList(newSelected);
	};

	const getSelectedList = () => {
		if (!selectedParCodeList.length) {
			return <span className="no-pars-selected">Select Parameters</span>;
		} else {
			return selectedParCodeList.map((par) => par.parCode).join(", ");

			// return `${selectedParCodeList.length} selected`;
		}
	};

	const isSelected = (par) => {
		return selectedParCodeList.some(
			(other) => other.parCode === par.parCode,
		);
	};

	const reorder = (index, newIndex) => {
		if (
			index < 0 ||
			index >= parCodeList.length ||
			newIndex < 0 ||
			newIndex >= parCodeList.length
		)
			return;
		const newList = [...parCodeList];
		newList[index] = parCodeList[newIndex];
		newList[newIndex] = parCodeList[index];
		setParCodeList(newList);
	};

	const mapParCodesToList = () => {
		return parCodeList.map((par, index) => (
			<ParCodeListEntry
				par={par}
				parIndex={index}
				isSelected={isSelected(par)}
				togglePar={togglePar}
				reorder={reorder}
			/>
		));
	};

	const selectAll = () => {
		const newSelected = [...parCodeList];
		newSelected.sort(compPars);
		setSelectedParCodeList(newSelected);
	};

	const unselectAll = () => {
		setSelectedParCodeList([]);
	};

	return (
		<div
			id="validation-par-code-selector-wrapper"
			className="stackplot-control-wrapper"
			ref={ref}
		>
			<div id="validation-par-code-selector">
				<div id="par-code-selector-display" onClick={toggleOpen}>
					{getSelectedList()}
				</div>
				<Collapse id="par-code-selector-collapse" isOpen={isOpen}>
					<div id="par-code-selector-list-container">
						{mapParCodesToList()}
					</div>
					<div id="par-code-selector-button-container">
						<Button onClick={selectAll}>Select All</Button>
						<Button onClick={unselectAll}>Unselect All</Button>
					</div>
				</Collapse>
			</div>
		</div>
	);
}

function ParCodeListEntry({ par, parIndex, isSelected, togglePar, reorder }) {
	const getAbbr = () => {
		if (!par?.parAbbr) return "";
		return par.parAbbr;
	};

	const handleClick = () => {
		togglePar(par);
	};

	return (
		<div className="validation-par-code-list-entry" onClick={handleClick}>
			<SortControls size="small" index={parIndex} updateFn={reorder} />
			<input type="checkbox" checked={isSelected} readOnly />
			<div className="validation-par-code-list-entry-abbr">
				{getAbbr()}
			</div>
		</div>
	);
}

function ValidationTable({ data, virtuosoRef, sortColumns, handleHeaderSort }) {
	const [size, setSize] = useState("half");

	const anchorIndex = useRef(null);
	const lastClickedIndex = useRef(null);

	const {
		dataChanges,
		setDataChanges,
		setDataChanged,
		sortedToDataMap,
		highlighted,
		setHighlighted,
		callbacks,
		parCodeRoundingMap,
	} = useContext(DataValidationContext);

	const getHeaders = () => {
		if (!data.length) return null;
		return (
			<tr>
				<th className="validation-table-row-select">
					<input
						type="checkbox"
						checked={anyHighlighted()}
						onClick={handleHeaderCheckboxClick}
						readOnly
					/>
				</th>
				{Object.keys(AQMET_COLUMNS).map((column) => (
					<th
						id={`validation-table-header-${column.replace(/([A-Z])/, "-$1").toLowerCase()}`}
					>
						{AQMET_COLUMNS[column]}
						<HeaderSortButtons
							sortValue={column}
							sortCallback={handleHeaderSort}
							currentSort={sortColumns}
						/>
					</th>
				))}
			</tr>
		);
	};

	const buildRow = (index, row) => (
		<>
			<td
				className="validation-table-row-select"
				onMouseDown={(e) => handleCheckboxClick(e, row, index)}
			>
				<input
					type="checkbox"
					checked={
						highlighted[row.recordDate]?.includes(row.parCode) ||
						false
					}
					readOnly
				/>
			</td>
			{COLUMN_LIST.map((column) => {
				if (EDITABLE_COLUMNS[column]) {
					return (
						<td
							className="validation-table-cell-editable"
							onClick={handleInputCellClick}
						>
							<input
								type="text"
								className={
									"validation-table-input" +
									(exists(dataChanges[index]?.[column]) &&
									dataChanges[index]?.[column] !== row[column]
										? " changed"
										: "")
								}
								placeholder={row[column]}
								onChange={(event) => {
									handleCellInput(
										event,
										index,
										ifExists(dataChanges[index], row),
										column,
									);
								}}
								value={dataChanges[index]?.[column]}
							/>
						</td>
					);
				} else {
					return (
						<td
							className={
								"validation-table-cell-readonly" +
								(exists(dataChanges[index]?.[column]) &&
								dataChanges[index]?.[column] !== row[column]
									? " changed"
									: "")
							}
						>
							{formatColumn(
								column,
								ifExists(
									dataChanges[index]?.[column],
									row[column],
								),
								row.parCode,
							)}
						</td>
					);
				}
			})}
		</>
	);

	const anyHighlighted = () => {
		return Object.keys(highlighted).length > 0;
	};

	const handleHeaderCheckboxClick = (event) => {
		const newHighlighted = {};
		if (!anyHighlighted()) {
			for (let row of data) {
				newHighlighted[row.recordDate] =
					newHighlighted[row.recordDate] || [];
				newHighlighted[row.recordDate].push(row.parCode);
			}
		}
		setHighlighted(newHighlighted);
	};

	const handleCheckboxClick = (event, row, index) => {
		event.preventDefault();
		event.stopPropagation();
		if (event.ctrlKey && event.shiftKey) {
			return;
		} else if (event.ctrlKey) {
			// Select multiple individual cells
			handleMultipleRowSelect(event, row, index);
		} else if (event.shiftKey) {
			// Select range of rows
			handleRangeRowSelect(event, row, index);
		} else {
			// Select single row
			handleSingleRowSelect(event, row, index);
		}
	};

	const handleSingleRowSelect = (event, row, index) => {
		let newPars = highlighted[row.recordDate] || [];
		let newHighlighted;
		if (newPars.includes(row.parCode)) {
			newHighlighted = { ...highlighted };
			newPars.splice(newPars.indexOf(row.parCode), 1);
		} else {
			newHighlighted = {};
			newPars.push(row.parCode);
		}
		if (newPars.length) {
			newHighlighted[row.recordDate] = newPars;
		} else {
			delete newHighlighted[row.recordDate];
		}
		setHighlighted(newHighlighted);
		lastClickedIndex.current = index;
		anchorIndex.current = index;
	};

	const handleMultipleRowSelect = (event, row, index) => {
		let newPars = highlighted[row.recordDate] || [];
		let newHighlighted = { ...highlighted };
		if (newPars.includes(row.parCode)) {
			newPars.splice(newPars.indexOf(row.parCode), 1);
		} else {
			newPars.push(row.parCode);
		}
		if (newPars.length) {
			newHighlighted[row.recordDate] = newPars;
		} else {
			delete newHighlighted[row.recordDate];
		}
		setHighlighted(newHighlighted);
		lastClickedIndex.current = index;
		anchorIndex.current = index;
	};

	const handleRangeRowSelect = (event, row, index) => {
		let newHighlighted = {};
		const range = data.slice(
			Math.min(index, anchorIndex.current),
			Math.max(index, anchorIndex.current) + 1,
		);
		if (index === lastClickedIndex.current) {
			let clear = true;
			for (let row of range) {
				if (!highlighted[row.recordDate]?.includes(row.parCode)) {
					clear = false;
					break;
				}
			}
			if (!clear) {
				for (let row of range) {
					newHighlighted[row.recordDate] =
						newHighlighted[row.recordDate] || [];
					newHighlighted[row.recordDate].push(row.parCode);
				}
			}
		} else {
			for (let row of range) {
				newHighlighted[row.recordDate] =
					newHighlighted[row.recordDate] || [];
				newHighlighted[row.recordDate].push(row.parCode);
			}
		}
		setHighlighted(newHighlighted);
		lastClickedIndex.current = index;
	};

	const formatColumn = (column, value, parCode) => {
		const formatFn = {
			recordDate: formatTimestamp,
			updateDate: formatTimestamp,
			insertDate: formatTimestamp,
			validatedVal: formatValidatedVal,
		};
		if (column in formatFn) {
			return formatFn[column](value, parCode);
		} else {
			return value;
		}
	};

	const formatTimestamp = (timestamp) => {
		return moment(timestamp).format("MM/DD/YYYY HH:mm");
	};

	const formatValidatedVal = (value, parCode) => {
		if (value === null) return value;
		if (!parCodeRoundingMap?.get(parCode)) return value;
		const { prec, trunc } = parCodeRoundingMap.get(parCode);
		if (trunc) {
			const fact = Math.pow(10, prec);
			return Math.trunc(fact * value) / fact;
		} else {
			return Number.parseFloat(value).toFixed(prec);
		}
	};

	const handleInputCellClick = (event) => {
		if (event.target.tagName !== "INPUT") {
			const el = event.target.querySelector("input");
			el.focus();
		}
	};

	const handleCellInput = (event, index, row, column) => {
		if (!document.activeElement === event.target) return;
		const value = event.target.value;
		const adjIndex = sortedToDataMap[index];
		setDataChanges((values) => ({
			...values,
			[adjIndex]: { ...row, [column]: value },
		}));
		setDataChanged(true);
	};

	return (
		<div id="validation-table-wrapper" className={size}>
			<div id="validation-table-controls">
				<span>Table View</span>
				<Button
					onClick={() => {
						setSize("minimized");
					}}
				>
					{utilIcons.minimized(colors["ars-neutral-900"])}
				</Button>
				<Button
					onClick={() => {
						setSize("half");
					}}
				>
					{utilIcons.halfScreen(colors["ars-neutral-900"])}
				</Button>
				<Button
					onClick={() => {
						setSize("full");
					}}
				>
					{utilIcons.fullScreen(colors["ars-neutral-900"])}
				</Button>
			</div>
			<div id="validation-table">
				<TableVirtuoso
					ref={virtuosoRef}
					style={{ height: "100%" }}
					data={data}
					components={{
						TableRow: TableRow,
					}}
					fixedHeaderContent={getHeaders}
					//itemContent={buildRow}
					context={{
						highlighted,
						callbacks,
						handleCheckboxClick,
						handleInputCellClick,
						dataChanges,
						formatColumn,
						handleCellInput,
					}}
				/>
			</div>
		</div>
	);
}

const TableRow = forwardRef(({ item, context, ...props }, ref) => {
	const index = props["data-index"];
	const row = item;
	const {
		highlighted,
		callbacks,
		handleCheckboxClick,
		handleInputCellClick,
		dataChanges,
		formatColumn,
		handleCellInput,
	} = context;
	return (
		<tr
			ref={ref}
			{...props}
			className={
				"validation-table-row" +
				(highlighted[item.recordDate]?.includes(item.parCode)
					? " highlighted"
					: "")
			}
			onContextMenu={callbacks.openPopover}
		>
			<td
				className="validation-table-row-select"
				onMouseDown={(e) => handleCheckboxClick(e, row, index)}
			>
				<input
					type="checkbox"
					checked={
						highlighted[row.recordDate]?.includes(row.parCode) ||
						false
					}
					readOnly
				/>
			</td>
			{COLUMN_LIST.map((column) => {
				if (EDITABLE_COLUMNS[column]) {
					return (
						<td
							className="validation-table-cell-editable"
							onClick={handleInputCellClick}
						>
							<input
								type="text"
								className={
									"validation-table-input" +
									(exists(dataChanges[index]?.[column]) &&
									dataChanges[index]?.[column] !== row[column]
										? " changed"
										: "")
								}
								placeholder={row[column]}
								onChange={(event) => {
									handleCellInput(
										event,
										index,
										ifExists(dataChanges[index], row),
										column,
									);
								}}
								value={dataChanges[index]?.[column]}
							/>
						</td>
					);
				} else {
					return (
						<td
							className={
								"validation-table-cell-readonly" +
								(exists(dataChanges[index]?.[column]) &&
								dataChanges[index]?.[column] !== row[column]
									? " changed"
									: "")
							}
						>
							{formatColumn(
								column,
								dataChanges[index]
									? dataChanges[index][column]
									: row[column],
								row.parCode,
							)}
						</td>
					);
				}
			})}
		</tr>
	);
});

function LoadingScreen({ hidden, loadingProgress = 0, isMobile = false }) {
	const getContent = () => {
		if (isMobile) {
			return (
				<div className="loading-wrapper">
					<progress value={loadingProgress} />
					<span>{Math.round(loadingProgress * 1000) / 10}%</span>
				</div>
			);
		}
	};

	return (
		<div
			className={"loading" + (isMobile ? " mobile" : "")}
			hidden={hidden}
		>
			{getContent()}
		</div>
	);
}

function DataFilterModal({
	isOpen,
	toggle,
	sortData,
	sortColumns,
	setSortColumns,
}) {
	const getUnselectedColumns = () => {
		const unselectedColumns = Object.keys(AQMET_COLUMNS).filter(
			(column) =>
				!sortColumns.some((sortColumn) => sortColumn[0] === column),
		);
		return unselectedColumns;
	};

	const mapSortColumnsToOption = () => {
		return sortColumns.map((sortColumn, index) => (
			<DataSortOption
				index={index}
				isLast={index === sortColumns.length - 1}
				sortColumns={sortColumns}
				setSortColumns={setSortColumns}
				createOption={createOption}
				deleteOption={deleteOption}
			/>
		));
	};

	const createOption = () => {
		const newSortColumns = [
			...sortColumns,
			[getUnselectedColumns()[0], "Asc"],
		];
		setSortColumns(newSortColumns);
	};

	const deleteOption = (index) => {
		const newSortColumns = [...sortColumns];
		newSortColumns.splice(index, 1);
		setSortColumns(newSortColumns);
	};

	const resetSortColumns = () => {
		setSortColumns([
			["recordDate", "Asc"],
			["parCode", "Asc"],
		]);
	};

	return (
		<Modal id="validation-filter-modal" isOpen={isOpen} toggle={toggle}>
			<ModalHeader toggle={toggle}>Sort/Filter Data</ModalHeader>
			<ModalBody>
				<div id="validation-sort-options-wrapper">
					<h4>Sort by:</h4>
					<div id="validation-sort-options-container">
						{mapSortColumnsToOption()}
					</div>
					<Button
						id="validation-sort-reset"
						onClick={resetSortColumns}
					>
						Reset Sort
					</Button>
				</div>
			</ModalBody>
		</Modal>
	);
}

function DataSortOption({
	index,
	isLast,
	sortColumns,
	setSortColumns,
	createOption,
	deleteOption,
}) {
	const getOptionValue = () => {
		const option = sortColumns[index];
		return option[0];
	};

	const getOptionDirection = () => {
		const option = sortColumns[index];
		return option[1];
	};

	const toggleDirection = () => {
		const newOption = [...sortColumns[index]];
		if (getOptionDirection() === "Asc") {
			newOption[1] = "Desc";
		} else {
			newOption[1] = "Asc";
		}
		const newSortColumns = [...sortColumns];
		newSortColumns[index] = newOption;
		setSortColumns(newSortColumns);
	};

	const getSuffix = () => {
		if (isLast) {
			return (
				<Button
					className="validation-sort-option-direction-toggle"
					onClick={createOption}
				>
					+
				</Button>
			);
		} else {
			return <span>then</span>;
		}
	};

	const mapUnselectedColumnsToOption = () => {
		return Object.keys(AQMET_COLUMNS)
			.filter(
				(column) =>
					getOptionValue() === column ||
					!sortColumns.some((sortColumn) => sortColumn[0] === column),
			)
			.map((column) => (
				<option value={column}>{AQMET_COLUMNS[column]}</option>
			));
	};

	const handleSelect = (event) => {
		const value = event.target.value;
		const newOption = [value, "Asc"];
		const newSortColumns = [...sortColumns];
		newSortColumns[index] = newOption;
		setSortColumns(newSortColumns);
	};

	return (
		<div className="validation-sort-option">
			<Button
				className="validation-sort-option-delete"
				disabled={sortColumns.length === 1}
				onClick={() => deleteOption(index)}
			>
				-
			</Button>
			<select value={getOptionValue()} onChange={handleSelect}>
				{mapUnselectedColumnsToOption()}
			</select>
			<Button
				className="validation-sort-option-direction"
				onClick={toggleDirection}
			>
				{getOptionDirection()}
			</Button>
			{getSuffix()}
		</div>
	);
}

function ValidationPopover({
	selectedSite,
	registerCallback,
	highlighted,
	applyValidationInfo,
	applyBatchChange,
}) {
	const [valCodeList, setValCodeList] = useState([]);
	const [selectedPars, setSelectedPars] = useState({});
	const [validationCode, setValidationCode] = useState("V");
	const [qualifierCode, setQualifierCode] = useState(null);
	const [controlVal, setControlVal] = useState(null);
	const [pos, setPos] = useState(null);
	const [visible, setVisible] = useState(false);
	const [expanded, setExpanded] = useState(false);

	const ref = useRef(null);

	const { sendGetRequest } = useContext(RestContext);
	const { parCodeList } = useContext(DataValidationContext);

	const POPOVER_WIDTH = 400;
	const POPOVER_HEIGHT = 250;

	const toggleExpanded = () => setExpanded(!expanded);

	useEffect(() => {
		registerCallback("openPopover", handleOpenEvent);
	}, []);

	useEffect(() => {
		sendGetRequest(
			VALIDATION_URL + "/valCodes/" + selectedSite,
			(response) => {
				const responseData = response.data;
				responseData.sort((a, b) =>
					a.validationCode > b.validationCode ? 1 : -1,
				);
				setValCodeList(response.data);
			},
		);
	}, [selectedSite]);

	useEffect(() => {
		setSelectedPars(
			Object.fromEntries(
				getHighlightedPars().map((par) => [par.parCode, true]),
			),
		);
		setValidationCode("V");
		setQualifierCode(null);
		setControlVal(null);
	}, [highlighted]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (ref.current && pos) {
				if (!ref.current.contains(event.target)) {
					setPos(null);
				}
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [ref, pos]);

	useEffect(() => {
		if (!pos) {
			setValidationCode("V");
			setQualifierCode(null);
			setControlVal(null);
			setExpanded(false);
		}
	}, [pos]);

	useEffect(() => {
		setVisible(!!highlighted && Object.keys(highlighted).length);
	}, [highlighted]);

	useEffect(() => {
		if (pos && expanded) {
			setPos({
				x: clamp(
					pos.x,
					10,
					window.innerWidth - (POPOVER_WIDTH * 2 + 44),
				),
				y: pos.y,
			});
		}
	}, [expanded]);

	const handleOpenEvent = (event) => {
		if (!event.shiftKey) {
			event.preventDefault();
			setPos({
				x: clamp(
					event.clientX - POPOVER_WIDTH / 2,
					10,
					window.innerWidth - (POPOVER_WIDTH + 44),
				),
				y: clamp(
					event.clientY - POPOVER_HEIGHT / 2,
					10,
					window.innerHeight - (POPOVER_HEIGHT + 10),
				),
			});
		}
	};

	const getHighlightedPars = () => {
		if (!parCodeList || !highlighted) return [];
		const highlightedParCodes = [];
		Object.keys(highlighted).forEach((timestamp) => {
			for (let parCode of highlighted[timestamp]) {
				if (!highlightedParCodes.includes(parCode)) {
					highlightedParCodes.push(parCode);
				}
			}
		});
		const highlightedPars = [];
		highlightedParCodes.forEach((parCode) => {
			const par = parCodeList.find(
				(foundPar) => foundPar.parCode === parCode,
			);
			if (par) {
				highlightedPars.push(par);
			}
		});
		return highlightedPars;
	};

	const getXPos = () => {
		const x = pos?.x || 0;
		return x;
	};

	const getYPos = () => {
		return pos?.y || 0;
	};

	const isChecked = (parCode) => {
		if (!selectedPars) return false;
		return selectedPars[parCode];
	};

	const toggleParCode = (parCode) => {
		setSelectedPars((values) => ({
			...values,
			[parCode]: !selectedPars[parCode],
		}));
	};

	const mapParCodesToList = () => {
		return getHighlightedPars().map((par) => (
			<div
				className="validation-popover-parcode"
				onClick={() => toggleParCode(par.parCode)}
			>
				<input
					type="checkbox"
					checked={isChecked(par.parCode)}
					readOnly
				/>
				<span>{par.parAbbr}</span>
			</div>
		));
	};

	const mapValCodesToList = () => {
		if (!valCodeList) return null;
		return valCodeList.map((valCode) => (
			<option
				value={valCode.validationCode}
			>{`${valCode.validationCode} - ${valCode.reason}`}</option>
		));
	};

	const isAdjusted = () => {
		return validationCode === "VA" || validationCode === "VM";
	};

	const isValidControlVal = () => {
		return !!controlVal?.match(/^[+*/=]?\-?\d+(\.\d+)?$/);
	};

	const handleTextInput = (event) => {
		const name = event.target.name;
		const value = event.target.value;
		if (name === "validationCode") {
			setValidationCode(value);
		} else if (name === "qualifierCode") {
			setQualifierCode(value);
		} else if (name === "controlVal") {
			setControlVal(value);
		}
	};

	const handleApply = () => {
		const payload = {
			selectedPars,
			validationCode,
			qualifierCode,
			controlVal,
		};
		applyValidationInfo(payload);
	};

	const canApply = () => {
		return !(
			(validationCode === "VA" && !isValidControlVal()) ||
			(validationCode === "VM" && isNaN(controlVal))
		);
	};

	return (
		<div
			id="validation-popover"
			ref={ref}
			hidden={!pos || !visible}
			style={{
				top: getYPos(),
				left: getXPos(),
				width: POPOVER_WIDTH * (expanded ? 2 : 1),
				height: POPOVER_HEIGHT,
			}}
		>
			<div id="validation-popover-main">
				<div id="validation-popover-left">
					<Label>Parameters</Label>
					<div id="validation-popover-parcode-container">
						{mapParCodesToList()}
					</div>
				</div>
				<div id="validation-popover-right">
					<div className="validation-popover-input-wrapper">
						<Label>Validation Code</Label>
						<select
							name={"validationCode"}
							value={validationCode}
							onChange={handleTextInput}
						>
							{mapValCodesToList()}
						</select>
					</div>
					<div className="validation-popover-input-wrapper">
						<Label>Qualifier Code(s)</Label>
						<input
							type="text"
							name="qualifierCode"
							value={qualifierCode || ""}
							onChange={handleTextInput}
						/>
					</div>
					<div
						className="validation-popover-input-wrapper"
						hidden={!isAdjusted()}
					>
						<Label>
							{validationCode === "VM"
								? "Maximum Value"
								: "Control Val"}
						</Label>
						<input
							type="text"
							name="controlVal"
							value={controlVal || ""}
							onChange={handleTextInput}
							pattern="^[+*/=]?\-?\d+(\.\d+)?$"
						/>
					</div>
					<Button
						id="validation-popover-apply-button"
						onClick={handleApply}
						disabled={!canApply()}
					>
						Apply
					</Button>
				</div>
			</div>
			<div id="validation-popover-extra" hidden={!expanded}>
				<Label>Batch Edit</Label>
				<BatchEditControl
					text={"Raw Val"}
					name={"rawVal"}
					type={"number"}
					handleBatchApply={applyBatchChange}
					inputWidth={8}
				/>
				<BatchEditControl
					text={"Qualifier Code"}
					name={"qualifierCode"}
					type={"text"}
					handleBatchApply={applyBatchChange}
				/>
				<BatchEditControl
					text={"Source Code"}
					name={"sourceCode"}
					type={"text"}
					handleBatchApply={applyBatchChange}
					inputWidth={2}
				/>
				<BatchEditControl
					text={"Status"}
					name={"status"}
					type={"text"}
					handleBatchApply={applyBatchChange}
					inputWidth={2}
				/>
			</div>
			<Button
				id="validation-popover-expand-button"
				onClick={toggleExpanded}
			>
				{utilIcons.caret(colors["ars-neutral-900"], {
					rotate: (expanded ? "" : "-") + "90deg",
				})}
			</Button>
		</div>
	);
}

function BatchEditControl({ text, name, type, handleBatchApply, inputWidth }) {
	const [value, setValue] = useState(null);

	const handleInput = (event) => {
		setValue(event.target.value || null);
	};

	return (
		<div className="validation-popover-batch-edit-wrapper">
			<Label>{text}</Label>
			<input
				style={{
					width: exists(inputWidth)
						? `calc(8px + ${inputWidth}ch)`
						: undefined,
				}}
				type={type}
				value={value}
				onChange={handleInput}
			/>
			<Button onClick={() => handleBatchApply(name, value)}>
				{utilIcons.caret(colors["ars-neutral-900"], {
					rotate: "-90deg",
				})}
			</Button>
		</div>
	);
}

export default DataValidation;
